import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem("access") || localStorage.getItem("token");
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

interface AttendanceRecord {
  date: string;
  checkin_time: string | null;
  checkout_time: string | null;
  status: "checked_in" | "checked_out";
}

/**
 * Format coordinate to ensure it doesn't exceed 10 digits total
 * Coordinates are typically -90 to 90 for latitude and -180 to 180 for longitude
 * We'll use 6 decimal places which ensures:
 * - Latitude (max 2 digits before decimal): 90.123456 = 8 digits total ✓
 * - Longitude (max 3 digits before decimal): 180.123456 = 9 digits total ✓
 * This gives ~0.1 meter precision which is sufficient for location tracking
 * @param coord - Coordinate value (latitude or longitude)
 * @returns Formatted coordinate with 6 decimal places
 */
const formatCoordinate = (coord: number): number => {
  // Round to 6 decimal places to ensure max 10 digits total
  return parseFloat(coord.toFixed(6));
};

/**
 * Mark attendance (check-in or check-out)
 * POST /delivery/attendance/mark/
 * 
 * First call = check-in (marks available, sets is_available=true)
 * Second call = check-out (marks unavailable, sets is_available=false)
 * 
 * @param latitude - Optional: Current latitude for check-in location
 * @param longitude - Optional: Current longitude for check-in location
 * 
 * Note: Blocked if orders are out_for_delivery
 */
export const markAttendance = async (latitude?: number, longitude?: number): Promise<any> => {
  const payload: any = {};
  
  // Include coordinates if provided, formatted to ensure max 10 digits
  if (latitude !== undefined && longitude !== undefined) {
    payload.latitude = formatCoordinate(latitude);
    payload.longitude = formatCoordinate(longitude);
  }
  
  try {
    const response = await axios.post(
      `${BASE_URL}/delivery/attendance/mark/`,
      payload,
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error: any) {
    // If axios throws an error, check if it has response data
    if (error.response?.data) {
      // Return the error response data so the component can handle it
      throw error.response.data;
    }
    // Otherwise, throw the error as-is
    throw error;
  }
};

/**
 * Get attendance history (last 30 days)
 * GET /api/v1/delivery/attendance/history/
 */
export const getAttendanceHistory = async (): Promise<AttendanceRecord[]> => {
  const response = await axios.get(`${BASE_URL}/delivery/attendance/history/`, {
    headers: getAuthHeaders(),
  });

  return response.data;
};

